import React, {useEffect, useMemo, useRef, useState} from "react";
import {Alert, Button, Checkbox, Col, Collapse, Divider, Drawer, Form, Input, Modal, Popconfirm, Row, Select, Space, Spin, Tag, Upload, message} from "antd";
import {DeleteOutlined, PlusOutlined, UploadOutlined} from "@ant-design/icons";
import {observer} from "mobx-react-lite";
import {ApiApartmentsService, ApiCheckerboardService, ApiComplexesService} from "../../../../../shared/api";
import {config} from "../../../../../shared/config/config";
import {authModel} from "../../../../../entities/auth";
import {complexOptions, loadReferences, mediaUrl, rememberComplex} from "./apartmentReferences";
import {ViewArrowsMap, geocode} from "./ViewArrowsMap";
import {InstallmentSchedule, scheduleTotal} from "./InstallmentSchedule";
import {FlatEdits} from "./FlatEdits";
import {FieldControl, fromForm, groupDigits, toForm} from "./fieldControls";
import {
  ANALYSIS_ROWS, COMPLEX_PHOTO_SLOTS, CONTACT_FIELDS, COORDS, COUNTRY_ROOMS, COUNTRY_ROWS, DOCUMENT_LINKS, DOCUMENT_ROWS, FEED_ROWS,
  FEED_TEXTS, LOCATION_ROWS, MAIN_ROWS, NOTE_FIELDS, OLD_FORM_FIELDS, PHOTO_ORDER, PHOTO_SLOTS, PLACE, REPORT_FIELDS,
  SECONDARY_BUILDER, SECONDARY_DEADLINE, oldChecks,
} from "./oldFlatLayout";

// A flat as on ECI old's add/edit form (oldFlatLayout.js): its fields, order, labels, lists and checks; saves only what
// changed. Columns the old form has not are under «Прочие поля», for admins. ECI old stays the source for its flats:
// its next change of the flat brings its values back.
const IMAGE_ACCEPT = '.jpg,.jpeg,.png,.webp,.gif,.jfif'

const money = (n) => `${groupDigits(String(Math.round(Number(n) || 0)))} ₽`

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b)

// the feed leaves a space before the longitude («59.92, 30.26» split at the comma)
const coordsText = (lat, lon) => (Number(lat) && Number(lon) ? `${String(lat).trim()}, ${String(lon).trim()}` : '')
// «59.93, 30.31» → ['59.93', '30.31']; null when it is not two numbers
const parseCoords = (text) => {
  const m = /^\s*(-?\d{1,3}(?:\.\d+)?)\s*[,; ]\s*(-?\d{1,3}(?:\.\d+)?)\s*$/.exec(String(text || '').replace(/,(?=\d)/g, '.'))
  return m ? [m[1], m[2]] : null
}
const coordsOf = (lat, lon) => (Number(lat) && Number(lon) ? [Number(lat), Number(lon)] : null)

const formValuesOf = (fields, apartment, placement) => {
  const values = {}
  fields.forEach(({key, kind}) => {
    values[key] = toForm(kind, apartment ? apartment[key] : null)
  })
  values[COORDS] = coordsText(apartment?.latitude, apartment?.longitude)
  values[PLACE.complex] = placement?.residentialComplexId || undefined
  values[PLACE.building] = placement?.buildingId || undefined
  values[PLACE.section] = placement?.section && placement.section !== '-' ? placement.section : ''
  return values
}

const errorOf = (error) => error?.response?.data?.errors || {}
const sameName = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase()

// a select's menu with «Добавить новый» under it (ECI old's «Добавить новый» for застройщик, ЖК, очередь, корпус)
const AddNewMenu = ({menu, placeholder, onAdd}) => {
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const add = async () => {
    const name = text.trim()
    if (!name) return
    setBusy(true)
    try {
      await onAdd(name)
      setText('')
    } catch (error) {
      message.error(errorOf(error).message || (error.response ? null : error.message) || 'Не удалось добавить')
    }
    setBusy(false)
  }
  return (
    <>
      {menu}
      <Divider style={{margin: '6px 0'}}/>
      <Space style={{padding: '0 8px 6px'}}>
        <Input size='small' placeholder={placeholder} value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') add() }}/>
        <Button size='small' type='link' icon={<PlusOutlined/>} loading={busy} onClick={add}>Добавить новый</Button>
      </Space>
    </>
  )
}

// a select's menu with a note under it instead of «Добавить новый»: a flat of ECI old's ЖК goes to ECI old's own queue
// and building, so one added only here would not save; action: «Добавить корпус на старом сайте»
const OldOnlyMenu = ({menu, note, action}) => (
  <>
    {menu}
    <Divider style={{margin: '6px 0'}}/>
    {action && <div style={{padding: '0 8px 4px'}}>{action}</div>}
    <div className='cell-hint' style={{padding: '0 8px 6px'}}>{note}</div>
  </>
)

// «Добавить корпус на старом сайте» (owner, 02.11): a page like the neighbouring korpus of the queue is made on ECI old,
// then the building here; dry — ECI old only answers what it would make
const OldKorpusModal = ({queues, queueId, deadlines, onDone, onClose}) => {
  const [form] = Form.useForm()
  const [busy, setBusy] = useState(false)
  const submit = async () => {
    const values = await form.validateFields()
    setBusy(true)
    try {
      const {data} = await ApiCheckerboardService.createOnOld({...values, number: values.number.trim(), deadline: values.deadline || ''})
      if (data.dry) {
        Modal.info({title: 'Пробный режим', content: data.message})
        onClose()
      } else {
        await onDone(data, values.number.trim())
      }
    } catch (error) {
      const failure = errorOf(error)
      if (failure.field && ['queueId', 'number', 'deadline'].includes(failure.field)) form.setFields([{name: failure.field, errors: [failure.message]}])
      else message.error(failure.message || 'Не удалось добавить корпус')
    }
    setBusy(false)
  }
  return (
    <Modal visible title='Новый корпус на старом сайте' okText='Добавить' cancelText='Отмена' confirmLoading={busy} onOk={submit} onCancel={onClose} destroyOnClose>
      <Form form={form} layout='vertical' initialValues={{queueId}}>
        <Form.Item name='queueId' label='Очередь' rules={[{required: true, message: 'Выберите очередь'}]}>
          <Select options={queues.map((q) => ({value: q.id, label: q.label}))} placeholder='Выберите'/>
        </Form.Item>
        <Form.Item name='number' label='Номер корпуса' rules={[{required: true, whitespace: true, message: 'Введите номер'}]}>
          <Input placeholder='например, 5.3' maxLength={20}/>
        </Form.Item>
        <Form.Item name='deadline' label='Срок сдачи корпуса'>
          <Select allowClear showSearch placeholder='как у соседнего корпуса' options={deadlines}/>
        </Form.Item>
      </Form>
      <div className='cell-hint'>
        На старом сайте появится страница корпуса в этой очереди, такая же, как у соседнего корпуса. Адрес, координаты, район,
        метро, тип дома, договор и отделка возьмутся с соседнего корпуса — поправить их можно на старом сайте.
      </div>
    </Modal>
  )
}

// pending: { file, url } chosen for a flat not added yet; fromComplex: value is the ЖК's picture, onTakeComplex puts it back
const PhotoSlot = ({label, value, pending, fromComplex, disabled, busy, onPick, onRemove, onTakeComplex}) => {
  const preview = pending ? pending.url : value ? mediaUrl(value) : null
  return (
    <div className='complex-file'>
      <div className='complex-file__head'><b>{label}</b>{fromComplex && !pending && <span className='cell-empty'> из ЖК</span>}</div>
      <div className='complex-file__body'>
        {preview
          ? <a href={preview} target='_blank' rel='noopener noreferrer'><img className='complex-file__image complex-file__image--contain' src={preview} alt={label}/></a>
          : <span className='cell-empty'>Нет фото</span>}
        <Space wrap>
          <Upload accept={IMAGE_ACCEPT} showUploadList={false} disabled={disabled || busy} beforeUpload={(file) => { onPick(file); return false }}>
            <Button size='small' icon={<UploadOutlined/>} loading={busy} disabled={disabled}>
              {fromComplex && !pending ? 'Загрузить другое' : preview ? 'Заменить' : 'Загрузить'}
            </Button>
          </Upload>
          {onTakeComplex && <Button size='small' disabled={disabled} onClick={onTakeComplex}>Взять из ЖК</Button>}
          {preview && (
            <Popconfirm title='Убрать фото?' okText='Убрать' cancelText='Нет' onConfirm={onRemove} disabled={disabled}>
              <Button size='small' danger icon={<DeleteOutlined/>} disabled={disabled || busy}>Удалить</Button>
            </Popconfirm>
          )}
        </Space>
      </div>
    </div>
  )
}

// readOnly: the user's roles give «Квартиры: просмотр» — the flat opens, nothing can be changed
export const ApartmentEditor = observer(({apartmentId, complexes: loadedComplexes, readOnly, onClose, onSaved, onCreated, onDeleted}) => {
  const [form] = Form.useForm()
  const isNew = apartmentId === 'new'
  const [references, setReferences] = useState(null)
  const [record, setRecord] = useState(null)
  const [loadError, setLoadError] = useState(null)
  // the chosen complex's queues with their buildings (the checkerboard's structure), and whether it is ECI old's ЖК
  const [queues, setQueues] = useState([])
  const [oldComplex, setOldComplex] = useState(false)
  const [korpusOnOld, setKorpusOnOld] = useState('off')
  const [addingKorpus, setAddingKorpus] = useState(false)
  const [saving, setSaving] = useState(false)
  const [busyField, setBusyField] = useState(null)
  const [dirty, setDirty] = useState(false)
  const [otherQuery, setOtherQuery] = useState('')
  // «Просмотреть изменения» (FlatEdits.jsx)
  const [showEdits, setShowEdits] = useState(false)
  // added here with «Добавить новый»
  const [addedComplexes, setAddedComplexes] = useState([])
  const [addedDevelopers, setAddedDevelopers] = useState([])
  const [developerFilter, setDeveloperFilter] = useState(undefined)
  // photos chosen before the flat exists: uploaded right after it is added
  const [pending, setPending] = useState({})
  const [pendingOther, setPendingOther] = useState([])
  // a new flat: «Генплан» and «Рендер» of its ЖК ({ field: file }), and the slots where they were removed
  const [complexPhotos, setComplexPhotos] = useState({})
  const [droppedComplex, setDroppedComplex] = useState([])
  const uploads = useRef(Promise.resolve())
  const geocoded = useRef('')

  const builderValue = Form.useWatch('builder', form)
  const rooms = Form.useWatch('numberOfRooms', form)
  const coordsValue = Form.useWatch(COORDS, form)
  const scheduleValue = Form.useWatch('installmentSchedule', form)
  const encumbrance = Form.useWatch('encumbranceBank', form)
  const paymentLeft = Form.useWatch('paymentLeft', form)
  const currentComplex = Form.useWatch(PLACE.complex, form)
  const currentQueue = Form.useWatch(PLACE.queue, form)

  const complexes = useMemo(() => [...loadedComplexes, ...addedComplexes.filter((a) => !loadedComplexes.some((c) => c.id === a.id))], [loadedComplexes, addedComplexes])
  const fields = useMemo(() => (references ? references.meta.groups.flatMap((g) => g.fields) : []), [references])
  const byKey = useMemo(() => Object.fromEntries(fields.map((f) => [f.key, f])), [fields])
  const formFields = useMemo(() => fields.filter((f) => f.kind !== 'photo' && f.kind !== 'files'), [fields])
  const lists = references?.meta.lists || {}

  useEffect(() => {
    let alive = true
    setLoadError(null)
    const request = isNew ? Promise.resolve(null) : ApiApartmentsService.fetchApartment(apartmentId)
    Promise.all([request, loadReferences()])
      .then(([response, refs]) => {
        if (!alive) return
        setReferences(refs)
        const data = response
          ? response.data
          : {apartment: {}, placement: {}, eciOldId: null, editable: refs.meta.editable, canPlace: refs.meta.placement}
        setRecord(data)
        const all = refs.meta.groups.flatMap((g) => g.fields).filter((f) => f.kind !== 'photo' && f.kind !== 'files')
        form.setFieldsValue(formValuesOf(all, data.apartment, data.placement))
        geocoded.current = data.apartment?.address || ''
        const placed = loadedComplexes.find((c) => c.id === data.placement?.residentialComplexId)
        setDeveloperFilter(data.apartment?.builder || placed?.developer || undefined)
        setDirty(false)
      })
      .catch(() => alive && setLoadError('Не удалось открыть квартиру. Обновите страницу.'))
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apartmentId])

  // the complex's queues and buildings; the flat's queue is the one its building is in
  const loadQueues = (complexId) => {
    if (!complexId) {
      setQueues([])
      setOldComplex(false)
      setKorpusOnOld('off')
      return Promise.resolve([])
    }
    return ApiCheckerboardService.fetchStructure(complexId)
      .then(({data}) => {
        const list = data.queues || []
        setQueues(list)
        setOldComplex(Boolean(data.complex && data.complex.eciOld))
        setKorpusOnOld(data.korpusOnOld || 'off')
        const building = form.getFieldValue(PLACE.building)
        const queue = building && list.find((q) => q.buildings.some((b) => b.id === building))
        if (queue && !form.getFieldValue(PLACE.queue)) form.setFieldsValue({[PLACE.queue]: queue.id})
        return list
      })
      .catch(() => {
        setQueues([])
        setOldComplex(false)
        setKorpusOnOld('off')
        return []
      })
  }

  useEffect(() => {
    if (record) loadQueues(record.placement?.residentialComplexId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.placement?.residentialComplexId, Boolean(record)])

  const editable = record ? record.editable : []
  const sees = (key) => Boolean(byKey[key])
  const canEdit = (key) => !readOnly && editable.includes(key)
  const canPlace = Boolean(!readOnly && record && record.canPlace)
  // a house, townhouse, cottage or plot chosen (for a flat being changed: chosen now): ECI old turns off the developer, ЖК,
  // queue and building, and neither sends nor requires them
  const countryLock = COUNTRY_ROOMS.includes(rooms) && (isNew || rooms !== record?.apartment?.numberOfRooms)
  const lockedByCountry = (name) => countryLock && [PLACE.complex, PLACE.queue, PLACE.building, 'builder'].includes(name)
  const canChange = (name) => (lockedByCountry(name) ? false
    : Object.values(PLACE).includes(name) ? canPlace : name === COORDS ? canEdit('latitude') : canEdit(name))
  const apartment = record ? record.apartment : {}
  const eciOldId = record ? record.eciOldId : null
  // stage 4: a save of an ECI old flat, and a new flat, go to ECI old first (backend eciOldSave.service)
  const savesOnOld = (eciOldId ? record?.eciOldSave : references?.meta?.eciOldSave) === 'live'

  const showError = (error, fallback) => {
    const {message: text, field} = errorOf(error)
    const name = field === 'residentialComplexId' ? PLACE.complex : field === 'buildingId' ? PLACE.building
      : field === 'latitude' || field === 'longitude' ? COORDS : field
    if (name && (formFields.some((f) => f.key === name) || Object.values(PLACE).includes(name) || name === COORDS)) form.setFields([{name, errors: [text]}])
    message.error(text || fallback)
  }

  const changesOf = () => {
    const values = form.getFieldsValue(true)
    const saved = formValuesOf(formFields, isNew ? {} : apartment, isNew ? {} : record.placement)
    const changes = {}
    formFields.forEach(({key, kind}) => {
      if (!canEdit(key) || key === 'latitude' || key === 'longitude') return
      const next = fromForm(kind, values[key])
      if (!same(next, fromForm(kind, saved[key]))) changes[key] = next
    })
    // «Координаты»: one input on ECI old, two columns here
    if (canEdit('latitude') && canEdit('longitude') && String(values[COORDS] || '').trim() !== String(saved[COORDS] || '').trim()) {
      const pair = parseCoords(values[COORDS])
      changes.latitude = pair ? pair[0] : null
      changes.longitude = pair ? pair[1] : null
    }
    const placement = {
      residentialComplexId: values[PLACE.complex] || null,
      buildingId: values[PLACE.building] || null,
      section: String(values[PLACE.section] || '').trim() || null,
    }
    const savedPlacement = {
      residentialComplexId: saved[PLACE.complex] || null,
      buildingId: saved[PLACE.building] || null,
      section: String(saved[PLACE.section] || '').trim() || null,
    }
    if (isNew || !same(placement, savedPlacement)) changes.placement = placement
    return changes
  }

  const applySaved = (data) => {
    const next = {...record, apartment: data.apartment, placement: data.placement || record.placement}
    setRecord(next)
    form.setFieldsValue(formValuesOf(formFields, next.apartment, next.placement))
    setDirty(false)
  }

  // ECI old's checks (oldFlatLayout.js), and coordinates that are two numbers
  const check = () => {
    const values = form.getFieldsValue(true)
    const can = (name) => canChange(name) && (Object.values(PLACE).includes(name) || sees(name))
    const errors = oldChecks(values, {isNew, can, scheduleTotal: scheduleTotal(values.installmentSchedule)})
    if (String(values[COORDS] || '').trim() && !parseCoords(values[COORDS])) errors.push({name: COORDS, message: 'Координаты: широта, долгота — например 59.939093, 30.249280'})
    if (!errors.length) return true
    form.setFields(errors.map(({name, message: text}) => ({name, errors: [text]})))
    Modal.error({title: 'Квартира не сохранена', content: <ul className='old-errors'>{errors.map((e) => <li key={e.name + e.message}>{e.message}</li>)}</ul>})
    return false
  }

  const uploadPending = async (id) => {
    const jobs = [...Object.entries(pending).map(([field, {file}]) => [field, file]), ...pendingOther.map(({file}) => ['otherPhotos', file])]
    let failed = 0
    for (const [field, file] of jobs) {
      try {
        await ApiApartmentsService.uploadPhoto(id, field, file)
      } catch (error) {
        failed += 1
      }
    }
    if (failed) message.warning(`Не загрузилось фото: ${failed}. Добавьте их в карточке квартиры.`)
  }

  const save = async () => {
    if (!check()) return
    const changes = changesOf()
    if (countryLock) {
      delete changes.builder
      if (isNew) changes.placement = {residentialComplexId: null, buildingId: null, section: changes.placement ? changes.placement.section : null}
      else delete changes.placement
    }
    const fromComplex = COMPLEX_PHOTO_SLOTS.filter((key) => complexPhotos[key] && !pending[key] && !droppedComplex.includes(key))
    if (isNew && !countryLock && fromComplex.length) changes.photosFromComplex = fromComplex
    if (!isNew && !Object.keys(changes).length) {
      message.info('Изменений нет')
      return
    }
    setSaving(true)
    try {
      if (isNew) {
        const {data} = await ApiApartmentsService.createApartment(changes)
        await uploadPending(data.apartment.id)
        message.success('Квартира добавлена')
        setDirty(false)
        onCreated(data.apartment)
      } else {
        const {data} = await ApiApartmentsService.updateApartment(apartment.id, changes)
        applySaved(data)
        message.success('Сохранено')
        onSaved()
      }
    } catch (error) {
      showError(error, 'Не удалось сохранить')
    }
    setSaving(false)
  }

  // uploads one after another (several photos at once keep their order); a new flat keeps them until it is added
  const upload = (field, file) => {
    if (isNew) {
      const picked = {file, url: URL.createObjectURL(file)}
      if (field === 'otherPhotos') setPendingOther((list) => [...list, picked])
      else setPending((map) => ({...map, [field]: picked}))
      return
    }
    uploads.current = uploads.current.then(async () => {
      setBusyField(field)
      try {
        const {data} = await ApiApartmentsService.uploadPhoto(apartment.id, field, file)
        setRecord((current) => ({...current, apartment: {...current.apartment, [field]: data.apartment[field]}}))
        onSaved()
      } catch (error) {
        showError(error, `Не удалось загрузить ${file.name}`)
      }
      setBusyField(null)
    })
  }

  const removePhoto = async (field, value = null) => {
    if (isNew) {
      setPending(({[field]: dropped, ...rest}) => rest)
      if (complexPhotos[field]) setDroppedComplex((list) => (list.includes(field) ? list : [...list, field]))
      return
    }
    setBusyField(field)
    try {
      const {data} = await ApiApartmentsService.updateApartment(apartment.id, {[field]: value})
      setRecord((current) => ({...current, apartment: {...current.apartment, [field]: data.apartment[field]}}))
      onSaved()
    } catch (error) {
      showError(error, 'Не удалось убрать')
    }
    setBusyField(null)
  }

  const remove = async () => {
    try {
      await ApiApartmentsService.deleteApartment(apartment.id)
      message.success('Квартира удалена')
      onDeleted(apartment.id)
    } catch (error) {
      showError(error, 'Не удалось удалить')
    }
  }

  const close = () => {
    if (!dirty) return onClose()
    Modal.confirm({
      title: 'Закрыть без сохранения?',
      content: 'Изменения в полях не сохранены.',
      okText: 'Закрыть',
      cancelText: 'Вернуться',
      onOk: onClose,
    })
  }

  // «Взять из ЖК»: the slot shows the ЖК's picture again
  const takeComplex = (field) => {
    setPending(({[field]: dropped, ...rest}) => rest)
    setDroppedComplex((list) => list.filter((key) => key !== field))
  }

  // a new flat: what its complex and building give, into the fields still empty (on ECI old the address came from the ЖК),
  // and its ЖК's «Генплан» and «Рендер»
  const fillDefaults = async (complexId, buildingId) => {
    if (!isNew) return
    if (!complexId) {
      setComplexPhotos({})
      return
    }
    try {
      const {data} = await ApiApartmentsService.fetchDefaults(complexId, buildingId)
      setComplexPhotos(Object.fromEntries(COMPLEX_PHOTO_SLOTS.map((key) => [key, data[key] || null])))
      const values = form.getFieldsValue(true)
      const next = {}
      Object.entries(data).forEach(([key, value]) => {
        if (key === 'latitude' || key === 'longitude' || COMPLEX_PHOTO_SLOTS.includes(key)) return
        const field = byKey[key]
        if (!field || !canEdit(key) || fromForm(field.kind, values[key]) !== null) return
        next[key] = toForm(field.kind, value)
      })
      if (data.latitude && data.longitude && canEdit('latitude') && !String(values[COORDS] || '').trim()) next[COORDS] = coordsText(data.latitude, data.longitude)
      if (next.address) geocoded.current = next.address
      form.setFieldsValue(next)
    } catch (error) {
      // the form works without them
    }
  }

  // a typed address: the coordinates (and the map) follow it
  const locateAddress = async () => {
    const address = String(form.getFieldValue('address') || '').trim()
    if (!address || address === geocoded.current || !canEdit('latitude')) return
    geocoded.current = address
    try {
      const point = await geocode(address)
      if (!point) return
      form.setFieldsValue({[COORDS]: `${point[0].toFixed(6)}, ${point[1].toFixed(6)}`})
      setDirty(true)
    } catch (error) {
      // no map: the coordinates stay as typed
    }
  }

  const seesBuilder = sees('builder')
  const developer = seesBuilder ? builderValue : developerFilter
  const secondary = (name) => String(name || '').trim() === SECONDARY_BUILDER
  // «Район» … «Тип»: on ECI old a new flat shows them until its developer is chosen, then only «Вторичный рынок» does
  const showLocation = isNew ? !String(developer || '').trim() || secondary(developer) : secondary(apartment.builder) && secondary(developer)
  const developers = [...new Set([...complexes.map((c) => c.developer).filter(Boolean), ...addedDevelopers, ...(developer ? [developer] : [])])]
    .sort((a, b) => a.localeCompare(b, 'ru'))
  const complexChoices = developer && complexes.some((c) => sameName(c.developer, developer))
    ? complexes.filter((c) => sameName(c.developer, developer))
    : complexes
  const currentCard = complexes.find((c) => c.id === currentComplex)
  const typedCoords = parseCoords(coordsValue)
  const mapCenter = (typedCoords && coordsOf(typedCoords[0], typedCoords[1])) || (currentCard && coordsOf(currentCard.latitude, currentCard.longitude))
  // a flat that goes to ECI old (a new one, or one of its flats) in ECI old's ЖК: only ECI old's buildings and their
  // queues — the portal's own rows of that ЖК (from earlier imports, or added here) are not on ECI old, and such a flat
  // would not save; the flat's present building stays in the list
  const oldPlaces = oldComplex && savesOnOld && (isNew || Boolean(eciOldId))
  const placedBuilding = record?.placement?.buildingId
  const placeQueues = oldPlaces
    ? queues
      .map((q) => ({...q, buildings: q.buildings.filter((b) => b.eciOldId || b.id === placedBuilding)}))
      .filter((q) => q.buildings.length)
    : queues
  const queueOf = (buildingId) => placeQueues.find((q) => q.buildings.some((b) => b.id === buildingId))
  const queueLabel = (q) => [q.name, q.endingperiod].filter(Boolean).join(' · ')
  // «Корпус»: the chosen queue's buildings; with no queue chosen, every building of the ЖК under its queue (choosing one
  // fills «Очередь»); each with its ECI old korpus page's own deadline, which is not its queue's (owner 02.11.2026)
  const chosenQueue = placeQueues.find((q) => q.id === currentQueue)
  const buildingOption = (b) => ({value: b.id, number: String(b.number), label: [b.number, b.deadline].filter(Boolean).join(' · ')})
  const buildingOptions = chosenQueue
    ? chosenQueue.buildings.map(buildingOption)
    : placeQueues.filter((q) => q.buildings.length).map((q) => ({
      label: `Очередь ${queueLabel(q)}`,
      options: q.buildings.map(buildingOption),
    }))

  // a developer chosen: its complexes only, and a complex of another developer is cleared; a complex chosen: its developer
  const chooseDeveloper = (value) => {
    setDeveloperFilter(value)
    const card = complexes.find((c) => c.id === form.getFieldValue(PLACE.complex))
    if (value && card && !sameName(card.developer, value)) {
      form.setFieldsValue({[PLACE.complex]: undefined, [PLACE.queue]: undefined, [PLACE.building]: undefined})
      setQueues([])
    }
  }

  const chooseComplex = (id) => {
    form.setFieldsValue({[PLACE.queue]: undefined, [PLACE.building]: undefined})
    loadQueues(id)
    const card = complexes.find((c) => c.id === id)
    if (card && card.developer) {
      setDeveloperFilter(card.developer)
      if (seesBuilder && canEdit('builder')) form.setFieldsValue({builder: card.developer})
    }
    fillDefaults(id)
  }

  // «Добавить новый»
  const addDeveloper = async (name) => {
    setAddedDevelopers((list) => [...list, name])
    if (seesBuilder && canEdit('builder')) form.setFieldsValue({builder: name})
    chooseDeveloper(name)
    setDirty(true)
  }
  const addComplex = async (name) => {
    let response
    try {
      response = await ApiComplexesService.createCard(developer ? {name, developer} : {name})
    } catch (error) {
      // a role that may not set a card's developer still adds the card
      if (errorOf(error).field !== 'developer') throw error
      response = await ApiComplexesService.createCard({name})
    }
    const card = response.data.card || response.data
    const item = {id: card.id, name: card.name, developer: card.developer ? String(card.developer).trim() : developer || '', latitude: card.latitude, longitude: card.longitude}
    setAddedComplexes((list) => [...list, item])
    rememberComplex(item)
    form.setFieldsValue({[PLACE.complex]: item.id})
    chooseComplex(item.id)
    setDirty(true)
    message.success(`ЖК «${item.name}» добавлен`)
  }
  const addQueue = async (name) => {
    const {data} = await ApiCheckerboardService.save('queue', null, {name, complexId: currentComplex})
    await loadQueues(currentComplex)
    form.setFieldsValue({[PLACE.queue]: data.id, [PLACE.building]: undefined})
    setDirty(true)
  }
  // a korpus made on ECI old (OldKorpusModal): chosen in the form with what it gives a flat
  const placeOldKorpus = async (data, number) => {
    await loadQueues(currentComplex)
    form.setFieldsValue({[PLACE.queue]: data.queueId, [PLACE.building]: data.building.id})
    fillDefaults(currentComplex, data.building.id)
    setDirty(true)
    setAddingKorpus(false)
    message.success(data.exists ? `Корпус ${number} уже был на старом сайте — выбран` : `Корпус ${number} добавлен на старом сайте`)
  }
  const addBuilding = async (number) => {
    if (!currentQueue) throw new Error('Сначала выберите очередь')
    const {data} = await ApiCheckerboardService.save('building', null, {number, queueId: currentQueue})
    await loadQueues(currentComplex)
    form.setFieldsValue({[PLACE.building]: data.id})
    fillDefaults(currentComplex, data.id)
    setDirty(true)
  }

  const loaded = Boolean(references && record)
  const otherFields = authModel.isAdmin ? formFields.filter((f) => !OLD_FORM_FIELDS.has(f.key)) : []
  const restricted = !readOnly && loaded && formFields.some(({key}) => OLD_FORM_FIELDS.has(key) && !canEdit(key))
  // as ECI old heads its form
  const title = isNew ? 'Создание квартиры' : `${record?.placement?.residentialComplex || ''} Редактировать ID ${apartment.id || ''}`.trim()
  const query = otherQuery.trim().toLowerCase()
  const firstPayment = apartment.firstPayment
  const total = scheduleTotal(scheduleValue)

  // one field of the old form: its label, the column's control (a select of ECI old's list where it has one)
  const item = (field, span = 8) => {
    const meta = byKey[field.key]
    if (!meta) return null
    return (
      <Col key={field.key} xs={24} md={span}>
        <Form.Item name={field.key} label={field.label}>
          <FieldControl field={meta} list={lists[field.key]} references={references} plain area={field.area} disabled={field.locked || !canEdit(field.key)}/>
        </Form.Item>
      </Col>
    )
  }
  const rows = (list) => list.map((row) => {
    const cells = row.map((field) => item(field)).filter(Boolean)
    return cells.length ? <Row key={row.map((f) => f.key).join()} gutter={16}>{cells}</Row> : null
  })
  const lines = (list) => list.map((field) => item(field, 24))
  const panel = (key, header, children) => {
    const content = children.flat().filter(Boolean)
    if (!content.length) return null
    return (
      <Collapse key={key} className='apartment-editor__other'>
        <Collapse.Panel header={header} key={key} forceRender>{content}</Collapse.Panel>
      </Collapse>
    )
  }
  const photoValue = (key) => apartment[key]
  const otherPhotos = apartment.otherPhotos || []

  return (
    <Drawer
      visible
      width={Math.min(1100, window.innerWidth)}
      title={apartment.stock ? <>{title} <Tag color='orange'>Запас</Tag></> : title}
      onClose={close}
      destroyOnClose
      className='complex-editor'
      footer={loaded && (
        <div className='complex-editor__footer'>
          {!readOnly && <Button type='primary' onClick={save} loading={saving}>Сохранить</Button>}
          <Button onClick={close}>Закрыть</Button>
          {!isNew && authModel.canView('flatEdits') && <Button onClick={() => setShowEdits(true)}>Просмотреть изменения</Button>}
          <span className='section-toolbar__spacer'/>
          {!isNew && !eciOldId && authModel.isAdmin && (
            <Popconfirm title='Удалить квартиру?' okText='Удалить' okButtonProps={{danger: true}} cancelText='Нет' onConfirm={remove}>
              <Button danger icon={<DeleteOutlined/>}>Удалить</Button>
            </Popconfirm>
          )}
        </div>
      )}
    >
      {loadError && <Alert type='error' message={loadError}/>}
      {showEdits && <FlatEdits apartmentId={apartment.id || apartmentId} onClose={() => setShowEdits(false)}/>}
      {addingKorpus && (
        <OldKorpusModal
          queues={placeQueues.map((q) => ({id: q.id, label: queueLabel(q)}))}
          queueId={currentQueue}
          deadlines={(lists.deadline || []).map((o) => ({value: o.label, label: o.label}))}
          onDone={placeOldKorpus}
          onClose={() => setAddingKorpus(false)}/>
      )}
      {!loadError && !loaded && <div className='complex-editor__loading'><Spin/></div>}
      {loaded && (
        <>
          {!isNew && (
            <div className='complex-editor__info'>
              <span>ID {apartment.id}</span>
              <span>{eciOldId ? `Со старого сайта, № ${eciOldId}` : 'Создана здесь'}</span>
              <a href={`${config.API_HOST}/apartments/${apartment.id}`} target='_blank' rel='noopener noreferrer'>Открыть на портале</a>
            </div>
          )}
          {eciOldId && (
            <Alert
              className='section-note'
              type={savesOnOld ? 'info' : 'warning'}
              showIcon
              message={savesOnOld
                ? 'Квартира со старого сайта. «Сохранить» сохраняет её сначала там — как кнопка «Сохранить» в форме старого сайта, с записью в журнале правок от вашего имени, — и только потом здесь. Если старый сайт не принял правку, она не сохраняется нигде.'
                : 'Квартира со старого сайта. Правка здесь продержится до следующего изменения этой квартиры на старом сайте или до ночной сверки — тогда вернутся значения оттуда.'}
            />
          )}
          {isNew && savesOnOld && (
            <Alert className='section-note' type='info' showIcon message='Квартира будет добавлена на старый сайт (как из его формы) и сразу появится здесь.'/>
          )}
          {restricted && <Alert className='section-note' type='info' showIcon message='Серые поля вашим ролям менять нельзя.'/>}
          <Form form={form} layout='vertical' className='old-flat-form' onValuesChange={(changed) => {
            setDirty(true)
            if ('builder' in changed) {
              chooseDeveloper(changed.builder)
              if (secondary(changed.builder) && byKey.deadline && canEdit('deadline')) form.setFieldsValue({deadline: toForm(byKey.deadline.kind, SECONDARY_DEADLINE)})
            }
            if (PLACE.complex in changed) chooseComplex(changed[PLACE.complex])
            if (PLACE.queue in changed) {
              const building = form.getFieldValue(PLACE.building)
              const queue = placeQueues.find((q) => q.id === changed[PLACE.queue])
              if (building && !(queue && queue.buildings.some((b) => b.id === building))) form.setFieldsValue({[PLACE.building]: undefined})
            }
            if (PLACE.building in changed && changed[PLACE.building]) {
              const queue = queueOf(changed[PLACE.building])
              if (queue && form.getFieldValue(PLACE.queue) !== queue.id) form.setFieldsValue({[PLACE.queue]: queue.id})
              fillDefaults(form.getFieldValue(PLACE.complex), changed[PLACE.building])
            }
          }}>
            {!isNew && sees('statuses') && (
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item name='statuses' label='Статус на портале'>
                    <FieldControl field={byKey.statuses} list={lists.statuses} references={references} disabled={!canEdit('statuses')}/>
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Row gutter={16}>
              <Col xs={24} md={8}>
                {seesBuilder
                  ? (
                    <Form.Item name='builder' label='Застройщик'>
                      <Select allowClear showSearch placeholder='Выберите' disabled={!canEdit('builder') || countryLock}
                        options={developers.map((d) => ({value: d, label: d}))}
                        dropdownRender={(menu) => <AddNewMenu menu={menu} placeholder='Новый застройщик' onAdd={addDeveloper}/>}/>
                    </Form.Item>
                  )
                  : (
                    <Form.Item label='Застройщик'>
                      <Select allowClear showSearch placeholder='Все застройщики' value={developerFilter} onChange={chooseDeveloper} disabled={countryLock}
                        options={developers.map((d) => ({value: d, label: d}))}/>
                    </Form.Item>
                  )}
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name={PLACE.complex} label='Название ЖК'>
                  <Select showSearch optionFilterProp='label' placeholder='Выберите' options={complexOptions(complexChoices)} disabled={!canPlace || countryLock}
                    dropdownRender={(menu) => <AddNewMenu menu={menu} placeholder='Новый ЖК' onAdd={addComplex}/>}/>
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name={PLACE.queue} label='Очередь'>
                  <Select allowClear showSearch optionFilterProp='label' placeholder='Выберите' disabled={!canPlace || !currentComplex || countryLock}
                    options={placeQueues.map((q) => ({value: q.id, label: queueLabel(q)}))}
                    dropdownRender={(menu) => (oldPlaces
                      ? <OldOnlyMenu menu={menu} note='Очереди этого ЖК на старом сайте (номер · срок сдачи).'/>
                      : <AddNewMenu menu={menu} placeholder='Новая очередь' onAdd={addQueue}/>)}/>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item name={PLACE.building} label='Корпус'>
                  <Select allowClear showSearch placeholder='корпус' disabled={!canPlace || !currentComplex || countryLock}
                    options={buildingOptions}
                    // by the building's number only, not its queue's name or deadline («6» is not «IV кв. 2026»)
                    filterOption={(input, option) => !option.options && String(option.number).toLowerCase().includes(input.trim().toLowerCase())}
                    dropdownRender={(menu) => (oldPlaces
                      ? (
                        <OldOnlyMenu menu={menu}
                          note={korpusOnOld === 'off'
                            ? 'Корпуса этого ЖК на старом сайте (номер · срок сдачи корпуса). Нужного нет — добавьте квартиру в этом корпусе на старом сайте, и корпус появится здесь.'
                            : 'Корпуса этого ЖК на старом сайте (номер · срок сдачи корпуса).'}
                          action={korpusOnOld !== 'off' && (
                            <Button size='small' type='link' icon={<PlusOutlined/>} onMouseDown={(e) => e.preventDefault()} onClick={() => setAddingKorpus(true)}>
                              Добавить корпус на старом сайте
                            </Button>
                          )}/>
                      )
                      : <AddNewMenu menu={menu} placeholder='Новый корпус' onAdd={addBuilding}/>)}/>
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name={PLACE.section} label='Секция'>
                  <Input disabled={!canPlace} placeholder='Секция'/>
                </Form.Item>
              </Col>
              {item({key: 'numberOfRooms', label: 'Кол-во комнат'})}
            </Row>

            {rows(MAIN_ROWS)}
            {lines(CONTACT_FIELDS)}

            <section className='complex-editor__group'>
              <h3>Загрузить фотографии</h3>
              <div className='complex-files'>
                {PHOTO_SLOTS.filter((p) => sees(p.key)).map((photo) => {
                  const ofComplex = isNew && !countryLock ? complexPhotos[photo.key] : null
                  const fromComplex = Boolean(ofComplex) && !droppedComplex.includes(photo.key)
                  const replaced = Boolean(ofComplex) && (Boolean(pending[photo.key]) || droppedComplex.includes(photo.key))
                  return (
                    <PhotoSlot
                      key={photo.key}
                      label={photo.label}
                      value={fromComplex ? ofComplex : photoValue(photo.key)}
                      pending={pending[photo.key]}
                      fromComplex={fromComplex}
                      disabled={!canEdit(photo.key)}
                      busy={busyField === photo.key}
                      onPick={(file) => upload(photo.key, file)}
                      onRemove={() => removePhoto(photo.key)}
                      onTakeComplex={replaced ? () => takeComplex(photo.key) : null}
                    />
                  )
                })}
              </div>
              {sees('otherPhotos') && (
                <div className='complex-file'>
                  <div className='complex-file__head'><b>Больше фото: {otherPhotos.length + pendingOther.length}</b></div>
                  <div className='complex-gallery'>
                    {otherPhotos.map((photo) => (
                      <div key={photo} className='complex-gallery__item'>
                        <a href={mediaUrl(photo)} target='_blank' rel='noopener noreferrer'><img src={mediaUrl(photo)} alt=''/></a>
                        {canEdit('otherPhotos') && (
                          <Popconfirm title='Убрать фото?' okText='Убрать' cancelText='Нет' onConfirm={() => removePhoto('otherPhotos', otherPhotos.filter((p) => p !== photo))}>
                            <button type='button' className='complex-gallery__remove' aria-label='Убрать фото' disabled={busyField === 'otherPhotos'}>×</button>
                          </Popconfirm>
                        )}
                      </div>
                    ))}
                    {pendingOther.map(({file, url}, i) => (
                      <div key={`${file.name}-${i}`} className='complex-gallery__item'>
                        <img src={url} alt=''/>
                        <button type='button' className='complex-gallery__remove' aria-label='Убрать фото'
                          onClick={() => setPendingOther((list) => list.filter((_, j) => j !== i))}>×</button>
                      </div>
                    ))}
                    <Upload accept={IMAGE_ACCEPT} multiple showUploadList={false} disabled={!canEdit('otherPhotos')}
                      beforeUpload={(file) => { upload('otherPhotos', file); return false }}>
                      <Button icon={<PlusOutlined/>} loading={busyField === 'otherPhotos'} disabled={!canEdit('otherPhotos')}>Больше фото +</Button>
                    </Upload>
                  </div>
                </div>
              )}
              <Row gutter={16}>{item(PHOTO_ORDER, 24)}</Row>
            </section>

            {panel('report', 'Поля для отчета продавцу (Нажмите чтобы открыть список)', [lines(REPORT_FIELDS)])}

            {(showLocation || COUNTRY_ROOMS.includes(rooms)) && (
              <section className='complex-editor__group'>
                {showLocation && rows(LOCATION_ROWS)}
                {COUNTRY_ROOMS.includes(rooms) && rows(COUNTRY_ROWS)}
              </section>
            )}

            <Row gutter={16}>
              {sees('address') && (
                <Col xs={24}>
                  <Form.Item name='address' label='Адрес'>
                    <Input disabled={!canEdit('address')} onBlur={locateAddress} onPressEnter={locateAddress}/>
                  </Form.Item>
                </Col>
              )}
              {sees('latitude') && (
                <Col xs={24}>
                  <Form.Item name={COORDS} label='Координаты'>
                    <Input disabled={!canEdit('latitude') || !canEdit('longitude')} placeholder='59.939093, 30.249280'/>
                  </Form.Item>
                </Col>
              )}
              {sees('coordsArrows') && (
                <Col xs={24}>
                  <Form.Item label='Вид из окон (стрелка)'>
                    <Form.Item name='coordsArrows' noStyle>
                      <ViewArrowsMap center={mapCenter} disabled={!canEdit('coordsArrows')}/>
                    </Form.Item>
                  </Form.Item>
                </Col>
              )}
              {lines(NOTE_FIELDS)}
            </Row>

            {sees('featureIds') && (
              <Form.Item name='featureIds' label='Особенности'>
                <Checkbox.Group className='old-features' options={references.features} disabled={!canEdit('featureIds')}/>
              </Form.Item>
            )}

            {rows(DOCUMENT_ROWS)}
            <Row gutter={16}>{lines(DOCUMENT_LINKS)}</Row>

            {sees('installmentSchedule') && (
              <Form.Item label='График рассрочки' extra={(
                <span>
                  Сумма графика: {money(total)}
                  {encumbrance === 'Рассрочка' && Number(paymentLeft || 0) !== total && ' · не равна «Остатку» — старый сайт так не сохранит'}
                  {firstPayment && (firstPayment.amount
                    ? ` · Первый взнос в рассрочку: ${money(firstPayment.amount)}`
                    : ' · Первый взнос в рассрочку: весь платёж по графику')}
                </span>
              )}>
                <Form.Item name='installmentSchedule' noStyle>
                  <InstallmentSchedule disabled={!canEdit('installmentSchedule')}/>
                </Form.Item>
              </Form.Item>
            )}

            <Row gutter={16}>{item({key: 'houseNumber', label: 'Номер квартиры'})}</Row>

            {panel('feed', 'Поля для работы с фидом', [rows(FEED_ROWS), <Row key='texts' gutter={16}>{lines(FEED_TEXTS)}</Row>])}
            {panel('analysis', 'Поля для работы с аналитикой', [rows(ANALYSIS_ROWS)])}

            {sees('postImage') && (
              <section className='complex-editor__group'>
                <h3>Оффер</h3>
                <div className='complex-files'>
                  <PhotoSlot label='Оффер' value={photoValue('postImage')} pending={pending.postImage} disabled={!canEdit('postImage') || !byKey.postImage || byKey.postImage.kind !== 'photo'}
                    busy={busyField === 'postImage'} onPick={(file) => upload('postImage', file)} onRemove={() => removePhoto('postImage')}/>
                </div>
              </section>
            )}

            {otherFields.length > 0 && (
              <Collapse className='apartment-editor__other'>
                <Collapse.Panel header={`Прочие поля: ${otherFields.length}`} key='other' forceRender>
                  <Input.Search allowClear className='apartment-editor__other-search' placeholder='Найти поле по названию'
                    value={otherQuery} onChange={(e) => setOtherQuery(e.target.value)}/>
                  <Row gutter={16}>
                    {otherFields.map((field) => (
                      <Col key={field.key} xs={24} md={12} lg={['long', 'list', 'ids', 'features'].includes(field.kind) ? 24 : 8}
                        style={query && !String(field.title).toLowerCase().includes(query) ? {display: 'none'} : undefined}>
                        <Form.Item name={field.key} label={field.title}>
                          <FieldControl field={field} references={references} disabled={!canEdit(field.key)}/>
                        </Form.Item>
                      </Col>
                    ))}
                  </Row>
                </Collapse.Panel>
              </Collapse>
            )}
          </Form>
        </>
      )}
    </Drawer>
  )
})
